import React, { useCallback, useState } from 'react';
import { Anohana, Btn, Bx, IconBtn, Typo } from '@curry-group/mui-curcuma';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { HeadlineContainer } from '../../components/headline-container';
import { ICommunicationAction } from '../../components/communication/header';

/**
 * Disclaimer shown in the bot-chat header (left of the session-save button on
 * desktop, under the title on mobile) for new, saved and broken-saved chats.
 */
export const BOT_CHAT_DISCLAIMER =
  'Der Medtec Online-Bot ist eine KI und kann Fehler machen. Diese Bot-Antwort wurde aus den Inhalten auf Medtec Online zusammengestellt.';

const INFO_HEADLINE = 'Über den Medtec Online-Chatbot';
const INFO_CONTENT_PREFIX = 'Der Medtec Online-Chatbot basiert ausschließlich auf den Inhalten der Plattform Medtec Online und wird bei STACKIT gehostet. Damit werden ihre Daten lokal am Standort Deutschland verarbeitet und sind sicher. Nutzen Sie den Chatbot um über kuratierte Inhalte der Plattform zu recherchieren und zu chatten. Bei Fragen wenden Sie sich an ';
const INFO_EMAIL = 'info@medteconline.de';

export interface IBotChatInfoButton {
  /**
   * Header `actions` entry — drop into `<Header actions={[...infoButton.action]} />`
   * to mirror the slot the regular chat header reserves for the video-conferencing
   * button.
   */
  action: ICommunicationAction;
  /**
   * Modal element to render somewhere inside the same component tree. Always
   * include this; it self-controls visibility.
   */
  modal: React.ReactNode;
}

/**
 * Adds an "info" icon button into the bot-chat header, opening a short modal
 * explaining what the chatbot is and where its data is processed.
 */
export function useBotChatInfoButton(): IBotChatInfoButton {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);

  const action: ICommunicationAction = {
    specificType: 'IconBtn',
    onClick: openModal,
    icon: (
      <FontAwesomeIcon
        icon={faInfoCircle}
        color="white"
        style={{ backgroundColor: '#004B76', borderRadius: '50%', cursor: 'pointer', fontSize: '1.8rem' }}
      />
    ),
    title: 'Informationen zum Chatbot',
    variant: 'text',
    color: 'secondary',
    size: 'small',
  };

  const modal = (
    <Anohana open={open} fullWidth maxWidth="sm">
      <HeadlineContainer
        bgcolor="background.paper"
        px={3}
        py={2.5}
        headline={INFO_HEADLINE}
        minHeight={81}
        headlineVariant="h4"
        headlineComponent="h5"
        endAdornment={<IconBtn size="small" onClick={closeModal}><FontAwesomeIcon icon={faTimes} /></IconBtn>}
      />
      <Bx py={3} px={3}>
        <Typo variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
          {INFO_CONTENT_PREFIX}
          <a href={`mailto:${INFO_EMAIL}`}>{INFO_EMAIL}</a>
          .
        </Typo>
        <Bx mt={3} display="flex" justifyContent="flex-end">
          <Btn onClick={closeModal} color="primary" size="large" variant="contained">
            Schließen
          </Btn>
        </Bx>
      </Bx>
    </Anohana>
  );

  return { action, modal };
}
